import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import API from "../../api/api";

const STATUS_FILTERS = [
  { label: "All Tickets", value: "all" },
  { label: "Open", value: "open" },
  { label: "In Progress", value: "in_progress" },
  { label: "Resolved", value: "resolved" },
  { label: "Closed", value: "closed" },
];

export default function AdminFranchiseSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successToast, setSuccessToast] = useState("");

  const [activeFilter, setActiveFilter] = useState("open");
  const [searchQuery, setSearchQuery] = useState("");

  // Selected Ticket for Addressal & Messaging
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [resolutionNoteInput, setResolutionNoteInput] = useState("");
  const [adminReplyInput, setAdminReplyInput] = useState("");
  const [savingAction, setSavingAction] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await API.get("/admin/support/tickets/", { params: { page_size: 100 } });
      const raw = res?.data?.results || res?.data || [];
      if (Array.isArray(raw)) {
        setTickets(raw);
      }
    } catch (e) {
      console.error("Fetch support tickets error:", e);
      setErrorMsg("Failed to load franchise support tickets. Please verify administrative permissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const stats = useMemo(() => {
    const total = tickets.length;
    const open = tickets.filter((t) => t.status === "open").length;
    const inProgress = tickets.filter((t) => t.status === "in_progress").length;
    const resolved = tickets.filter((t) => t.status === "resolved").length;
    const closed = tickets.filter((t) => t.status === "closed").length;
    return { total, open, inProgress, resolved, closed };
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      // Status filter
      if (activeFilter !== "all" && t.status !== activeFilter) {
        return false;
      }
      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const tid = String(t.id || "");
        const sub = String(t.subject || "").toLowerCase();
        const phone = String(t.phone || "").toLowerCase();
        const name = String(t.full_name || t.username || "").toLowerCase();
        const pin = String(t.pincode || "").toLowerCase();
        return (
          tid.includes(q) ||
          sub.includes(q) ||
          phone.includes(q) ||
          name.includes(q) ||
          pin.includes(q)
        );
      }
      return true;
    });
  }, [tickets, activeFilter, searchQuery]);

  // Handle Status Update (with optional resolution note)
  const handleUpdateStatus = async (newStatus) => {
    if (!selectedTicket) return;
    setSavingAction(true);
    try {
      const patchData = { status: newStatus };
      if (resolutionNoteInput.trim()) {
        patchData.resolution_note = resolutionNoteInput.trim();
      }
      const res = await API.patch(`/admin/support/tickets/${selectedTicket.id}/`, patchData);
      if (res?.data) {
        setSuccessToast(`Ticket #${selectedTicket.id} marked as ${newStatus.toUpperCase()}!`);
        setSelectedTicket(res.data);
        await fetchTickets();
      }
    } catch (e) {
      console.error("Update ticket status error:", e);
      setErrorMsg("Failed to update ticket status.");
    } finally {
      setSavingAction(false);
    }
  };

  // Handle Admin Message Reply
  const handleSendAdminReply = async () => {
    if (!selectedTicket || !adminReplyInput.trim()) return;
    setSavingAction(true);
    try {
      await API.post(`/admin/support/tickets/${selectedTicket.id}/messages/`, {
        message: adminReplyInput.trim(),
      });
      setAdminReplyInput("");
      setSuccessToast("Reply dispatched to franchise partner!");
      // Reload ticket details
      const updatedRes = await API.get(`/admin/support/tickets/${selectedTicket.id}/`).catch(() => null);
      if (updatedRes?.data) {
        setSelectedTicket(updatedRes.data);
      }
      await fetchTickets();
    } catch (e) {
      console.error("Admin reply error:", e);
      setErrorMsg("Failed to send reply to ticket.");
    } finally {
      setSavingAction(false);
    }
  };

  const openTicketDetail = (ticket) => {
    setSelectedTicket(ticket);
    setResolutionNoteInput(ticket.resolution_note || "");
    setAdminReplyInput("");
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: { xs: 1.5, sm: 3 } }}>
      {/* ── TOP HEADER ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: "#FFFFFF",
          border: "1px solid #EEF2F6",
          mb: 2.5,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "#5F259F", color: "#FFFFFF", width: 44, height: 44 }}>
              <SupportAgentRoundedIcon sx={{ fontSize: 26 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 950, color: "#0F172A", letterSpacing: "-0.01em" }}>
                Franchise Support & Grievance Addressal
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                Manage, investigate, and resolve support requests from Pincode, District & State Agencies
              </Typography>
            </Box>
          </Stack>
          <Button
            size="small"
            startIcon={<RefreshRoundedIcon />}
            onClick={fetchTickets}
            disabled={loading}
            sx={{
              borderRadius: "10px",
              border: "1px solid #CBD5E1",
              color: "#334155",
              fontWeight: 800,
              textTransform: "none",
              px: 2,
            }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Stack>

        {/* ── KPI METRICS SUMMARY ROW ── */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(5, 1fr)" }, gap: 1.5, mt: 2.5 }}>
          <Box sx={{ p: 1.5, bgcolor: "#F8FAFC", borderRadius: "14px", border: "1px solid #E2E8F0" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748B" }}>Total Tickets</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 950, color: "#0F172A", mt: 0.2 }}>{stats.total}</Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: "#FEF2F2", borderRadius: "14px", border: "1px solid #FECACA" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#DC2626" }}>🔴 Open</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 950, color: "#B91C1C", mt: 0.2 }}>{stats.open}</Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: "#FFFBEB", borderRadius: "14px", border: "1px solid #FDE68A" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#D97706" }}>🟡 In Progress</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 950, color: "#B45309", mt: 0.2 }}>{stats.inProgress}</Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: "#ECFDF5", borderRadius: "14px", border: "1px solid #A7F3D0" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#059669" }}>🟢 Resolved</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 950, color: "#047857", mt: 0.2 }}>{stats.resolved}</Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: "#F1F5F9", borderRadius: "14px", border: "1px solid #CBD5E1" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>Closed</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 950, color: "#334155", mt: 0.2 }}>{stats.closed}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* ── NOTIFICATIONS & FEEDBACK ── */}
      {successToast && (
        <Alert severity="success" onClose={() => setSuccessToast("")} sx={{ mb: 2, borderRadius: "12px", fontWeight: 700 }}>
          {successToast}
        </Alert>
      )}
      {errorMsg && (
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ mb: 2, borderRadius: "12px", fontWeight: 700 }}>
          {errorMsg}
        </Alert>
      )}

      {/* ── FILTER TABS & SEARCH BAR ── */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: "18px", bgcolor: "#FFFFFF", border: "1px solid #EEF2F6", mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
          {/* Status Filter Chips */}
          <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: { xs: 1, md: 0 } }}>
            {STATUS_FILTERS.map((f) => {
              const isSelected = activeFilter === f.value;
              return (
                <Chip
                  key={f.value}
                  label={f.label}
                  clickable
                  onClick={() => setActiveFilter(f.value)}
                  sx={{
                    fontWeight: 800,
                    fontSize: 12,
                    bgcolor: isSelected ? "#5F259F" : "#F1F5F9",
                    color: isSelected ? "#FFFFFF" : "#475569",
                    borderRadius: "10px",
                    "&:hover": { bgcolor: isSelected ? "#4D1A85" : "#E2E8F0" },
                  }}
                />
              );
            })}
          </Stack>

          {/* Search Box */}
          <TextField
            size="small"
            placeholder="Search by ticket #, phone, name, or PIN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: { xs: "100%", md: 320 }, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
        </Stack>
      </Paper>

      {/* ── TICKETS LIST ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#5F259F" }} />
        </Box>
      ) : filteredTickets.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", borderRadius: "20px", bgcolor: "#FFFFFF", border: "1px solid #EEF2F6" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#0F172A", mb: 0.5 }}>
            No Franchise Support Tickets Found
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748B" }}>
            {searchQuery ? "Try refining your search keyword or clearing the filter." : "There are currently no tickets under this status."}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {filteredTickets.map((t) => {
            const isResolved = t.status === "resolved";
            const isInProgress = t.status === "in_progress";
            const isClosed = t.status === "closed";
            const statusBg = isResolved ? "#ECFDF5" : isInProgress ? "#FFFBEB" : isClosed ? "#F1F5F9" : "#FEF2F2";
            const statusColor = isResolved ? "#059669" : isInProgress ? "#D97706" : isClosed ? "#475569" : "#DC2626";

            return (
              <Paper
                key={t.id}
                elevation={0}
                onClick={() => openTicketDetail(t)}
                sx={{
                  p: 2,
                  borderRadius: "16px",
                  bgcolor: "#FFFFFF",
                  border: "1px solid #EEF2F6",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#CBD5E1",
                    boxShadow: "0 4px 14px rgba(15,23,42,0.06)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: 13, fontWeight: 950, color: "#5F259F" }}>
                      #SUP-{t.id}
                    </Typography>
                    <Box
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        bgcolor: statusBg,
                        color: statusColor,
                        borderRadius: "999px",
                        px: 1.2,
                        py: 0.2,
                        textTransform: "uppercase",
                      }}
                    >
                      {t.status.replace("_", " ")}
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>
                    {t.created_at ? new Date(t.created_at).toLocaleString() : "-"}
                  </Typography>
                </Stack>

                <Typography sx={{ fontSize: 14.5, fontWeight: 900, color: "#0F172A", mb: 0.6 }}>
                  {t.subject || "Support Inquiry"}
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 2 }} alignItems={{ sm: "center" }}>
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    <PhoneRoundedIcon sx={{ fontSize: 14, color: "#64748B" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>
                      {t.phone || t.username || "Franchise Partner"} ({t.full_name || "Franchise"})
                    </Typography>
                  </Stack>
                  {t.pincode && (
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <LocationOnRoundedIcon sx={{ fontSize: 14, color: "#64748B" }} />
                      <Typography sx={{ fontSize: 11.5, color: "#64748B", fontWeight: 700 }}>
                        PIN {t.pincode} • {t.state_name || "Karnataka"}
                      </Typography>
                    </Stack>
                  )}
                  {Array.isArray(t.messages) && t.messages.length > 0 && (
                    <Typography sx={{ fontSize: 11, color: "#5F259F", fontWeight: 800 }}>
                      💬 {t.messages.length} replies
                    </Typography>
                  )}
                </Stack>

                {t.resolution_note && (
                  <Box sx={{ mt: 1.2, p: 1, bgcolor: "#F0FDF4", borderRadius: "10px", border: "1px solid #BBF7D0" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#166534" }}>
                      💡 Resolution: {t.resolution_note}
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* ── ADDRESSAL & CONVERSATION DIALOG ── */}
      <Dialog
        open={Boolean(selectedTicket)}
        onClose={() => setSelectedTicket(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "20px", p: 1 } }}
      >
        {selectedTicket && (
          <>
            <DialogTitle sx={{ pb: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#5F259F" }}>
                    #SUP-{selectedTicket.id} • ADDRESSAL CONSOLE
                  </Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 950, color: "#0F172A" }}>
                    {selectedTicket.subject}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setSelectedTicket(null)}>
                  <CloseRoundedIcon />
                </IconButton>
              </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 2 }}>
              {/* Partner Info Banner */}
              <Box sx={{ p: 1.5, bgcolor: "#F8FAFC", borderRadius: "14px", border: "1px solid #E2E8F0", mb: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748B" }}>Franchise Partner:</Typography>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: "#0F172A" }}>
                      {selectedTicket.full_name || selectedTicket.username} (Phone: {selectedTicket.phone})
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748B" }}>Jurisdiction / Location:</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#334155" }}>
                      PIN: {selectedTicket.pincode || "-"} • State: {selectedTicket.state_name || "Karnataka"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748B" }}>Current Status:</Typography>
                    <Chip
                      size="small"
                      label={selectedTicket.status.toUpperCase()}
                      sx={{
                        fontWeight: 900,
                        fontSize: 10.5,
                        bgcolor: selectedTicket.status === "resolved" ? "#ECFDF5" : selectedTicket.status === "in_progress" ? "#FFFBEB" : "#FEF2F2",
                        color: selectedTicket.status === "resolved" ? "#059669" : selectedTicket.status === "in_progress" ? "#D97706" : "#DC2626",
                      }}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Original Message */}
              <Box sx={{ mb: 2.5, p: 2, bgcolor: "#FFFFFF", borderRadius: "14px", border: "1px solid #E2E8F0" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#64748B", mb: 0.5, textTransform: "uppercase" }}>
                  Grievance Description / Ticket Message:
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#1E293B", whiteSpace: "pre-wrap" }}>
                  {selectedTicket.message || "No description provided."}
                </Typography>
              </Box>

              {/* Status Action Buttons */}
              <Box sx={{ mb: 2.5, p: 2, bgcolor: "#FAF5FF", borderRadius: "14px", border: "1px solid #E9D5FF" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#6B21A8", mb: 1, textTransform: "uppercase" }}>
                  Take Administrative Action:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  <Button
                    size="small"
                    variant={selectedTicket.status === "in_progress" ? "contained" : "outlined"}
                    startIcon={<HourglassEmptyRoundedIcon />}
                    disabled={savingAction}
                    onClick={() => handleUpdateStatus("in_progress")}
                    sx={{ borderRadius: "10px", fontWeight: 800, textTransform: "none", bgcolor: selectedTicket.status === "in_progress" ? "#D97706" : "transparent", borderColor: "#D97706", color: selectedTicket.status === "in_progress" ? "#FFFFFF" : "#D97706" }}
                  >
                    Mark In Progress
                  </Button>
                  <Button
                    size="small"
                    variant={selectedTicket.status === "resolved" ? "contained" : "outlined"}
                    startIcon={<CheckCircleRoundedIcon />}
                    disabled={savingAction}
                    onClick={() => handleUpdateStatus("resolved")}
                    sx={{ borderRadius: "10px", fontWeight: 800, textTransform: "none", bgcolor: selectedTicket.status === "resolved" ? "#059669" : "transparent", borderColor: "#059669", color: selectedTicket.status === "resolved" ? "#FFFFFF" : "#059669" }}
                  >
                    Resolve Ticket
                  </Button>
                  <Button
                    size="small"
                    variant={selectedTicket.status === "closed" ? "contained" : "outlined"}
                    startIcon={<DoneAllRoundedIcon />}
                    disabled={savingAction}
                    onClick={() => handleUpdateStatus("closed")}
                    sx={{ borderRadius: "10px", fontWeight: 800, textTransform: "none", bgcolor: selectedTicket.status === "closed" ? "#334155" : "transparent", borderColor: "#334155", color: selectedTicket.status === "closed" ? "#FFFFFF" : "#334155" }}
                  >
                    Close Ticket
                  </Button>
                  {selectedTicket.status !== "open" && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={savingAction}
                      onClick={() => handleUpdateStatus("open")}
                      sx={{ borderRadius: "10px", fontWeight: 800, textTransform: "none", color: "#DC2626", borderColor: "#DC2626" }}
                    >
                      Reopen
                    </Button>
                  )}
                </Stack>

                {/* Resolution Note Input */}
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#6B21A8", mb: 0.5 }}>
                    Resolution Note (Visible to Franchise Partner):
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. Payout credited to main wallet under reference #TXN9921"
                      value={resolutionNoteInput}
                      onChange={(e) => setResolutionNoteInput(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#FFFFFF" } }}
                    />
                    <Button
                      variant="contained"
                      disabled={savingAction || !resolutionNoteInput.trim()}
                      onClick={() => handleUpdateStatus(selectedTicket.status)}
                      sx={{ borderRadius: "10px", bgcolor: "#6B21A8", fontWeight: 800, textTransform: "none", whiteSpace: "nowrap" }}
                    >
                      Save Note
                    </Button>
                  </Stack>
                </Box>
              </Box>

              {/* Message Thread */}
              <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#0F172A", mb: 1 }}>
                Communication Thread with Franchise Partner:
              </Typography>
              {Array.isArray(selectedTicket.messages) && selectedTicket.messages.length > 0 ? (
                <Stack spacing={1.2} sx={{ mb: 2, maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
                  {selectedTicket.messages.map((m, mIdx) => {
                    const isAdmin = m.author_username?.toLowerCase().includes("admin") || m.author === 1;
                    return (
                      <Box
                        key={m.id || mIdx}
                        sx={{
                          p: 1.5,
                          borderRadius: "14px",
                          bgcolor: isAdmin ? "#EFF6FF" : "#F8FAFC",
                          border: "1px solid",
                          borderColor: isAdmin ? "#BFDBFE" : "#E2E8F0",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 900, color: isAdmin ? "#1D4ED8" : "#475569" }}>
                            {isAdmin ? "🛡️ Operations Desk (Admin)" : `👤 ${m.author_username || "Franchise Partner"}`}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: "#94A3B8" }}>
                            {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 12.5, color: "#1E293B" }}>
                          {m.message}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", mb: 2 }}>
                  No messages exchanged yet in this ticket.
                </Typography>
              )}

              {/* Admin Reply Box */}
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "#475569", mb: 0.5 }}>
                  Send Reply Message to Franchise:
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type official reply to franchise partner..."
                    value={adminReplyInput}
                    onChange={(e) => setAdminReplyInput(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#FFFFFF" } }}
                  />
                  <Button
                    variant="contained"
                    disabled={savingAction || !adminReplyInput.trim()}
                    onClick={handleSendAdminReply}
                    startIcon={<SendRoundedIcon />}
                    sx={{ borderRadius: "12px", bgcolor: "#5F259F", fontWeight: 800, textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Reply
                  </Button>
                </Stack>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setSelectedTicket(null)}
                sx={{ borderRadius: "10px", fontWeight: 800, color: "#64748B", textTransform: "none" }}
              >
                Close Window
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
